/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnInit} from '@angular/core';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {NOTES_BUTTONS} from '../../common/directives/notes/notes-constants';
import {ReleaseNotesHelper} from '../../../components/util/release-notes-helper.service';
import {Uuid} from '../../../components/util/uuid';
import template from './release-notes-edit-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {createBufferedFlag} from '../../common/util/template-state';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-release-notes-edit-page',
  template
})
export class ReleaseNotesEditPageComponent extends AdminResourceEditPageBase implements OnInit {
  readonly toolbarButtons = NOTES_BUTTONS;
  readonly apps = ['MARKETPLACE', 'ADSERVER', 'DATA'];
  readonly tags = ['FEATURE', 'BUGFIX', 'INFO', 'UPDATE'];

  loaded = false;
  private readonly loadedFlag = createBufferedFlag();
  loadError = '';
  publishMode = 'IMMEDIATE';
  visibility = 'PUBLIC';

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly releaseNotesHelper: ReleaseNotesHelper,
    private readonly $state: StateService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  get displayLoaded(): boolean {
    return this.bufferTemplateFlag(this.loadedFlag, !!this.loaded);
  }

  ngOnInit(): void {
    const id = this.$state.params?.id;
    const creating = this.isNew === true || id === 'new';
    if (creating) {
      this.isNew = true;
      this.applyModel({
        id: Uuid.generate(),
        contentType: 'HTML',
        status: 'DRAFT',
        state: 'ACTIVE',
        apps: ['MARKETPLACE', 'ADSERVER'],
        tags: [],
        userGroups: [],
        assetStrings: []
      });
      return;
    }

    if (!id) {
      this.loadError = 'Missing release note id';
      this.loaded = true;
      return;
    }

    this.releaseNotesHelper.getFromId(id).then((model: any) => {
      this.applyModel(model);
    }, (err: any) => {
      this.loadError = err?.message || 'Failed to load release note';
      this.loaded = true;
    });
  }

  ngOnChanges(): void {
    if (this.model) {
      this.attachSavePartial();
    }
    super.ngOnChanges();
    if (this.model) {
      this.loaded = true;
      this.loadError = '';
    }
  }

  onAssetStringsChange(assetStrings: string[]): void {
    this.editingModel.assetStrings = assetStrings;
    this.markExternalFormDirty();
  }

  toggleArrayValue(values: string[], value: string): void {
    const index = values.indexOf(value);
    if (index >= 0) {
      values.splice(index, 1);
    } else {
      values.push(value);
    }
    this.markExternalFormDirty();
  }

  onPublishModeChange(): void {
    if (this.publishMode === 'IMMEDIATE') {
      this.editingModel.publishTime = null;
    }
  }

  protected afterInitEditingModel(): void {
    this.editingModel.apps = this.editingModel.apps || [];
    this.editingModel.tags = this.editingModel.tags || [];
    this.editingModel.userGroups = this.editingModel.userGroups || [];
    this.editingModel.assetStrings = this.editingModel.assetStrings || [];
    this.publishMode = this.editingModel.publishTime ? 'CUSTOM' : 'IMMEDIATE';
    this.visibility = this.editingModel.userGroups.length ? 'INTERNAL' : 'PUBLIC';
  }

  protected onBeforeSubmit(): void {
    if (this.publishMode === 'IMMEDIATE') {
      this.editingModel.publishTime = null;
    }
    this.editingModel.userGroups = this.visibility === 'INTERNAL' ? ['INTERNAL'] : [];
  }

  private applyModel(model: any): void {
    this.model = model;
    this.attachSavePartial();
    this.ngOnChanges();
  }

  private attachSavePartial(): void {
    this.model.savePartial = () => this.releaseNotesHelper.post(this.model);
  }
}
